import React, {useEffect, useRef, useState} from 'react';
import {findProductsByQueryString, getCompanyByUserUUID, getSaleById, updateSale} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import {useNavigate, useParams} from "react-router-dom";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";

const SaleManagerEditItems = () => {
    const {saleId} = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const [sale, setSale] = useState(null);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [queryString, setQueryString] = useState('');
    const inputQueryString = useRef(null);
    const [totalValue, setTotalValue] = useState(0);
    const [deliveryEnabled, setDeliveryEnabled] = useState(false);

    useEffect(() => {
        getCompanyByUserUUID().then((company) => {
            if (company) {
                setDeliveryEnabled(!!company.deliveryEnabled);
            }
        });
    }, []);

    function removeSpecialChars(str) {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    }

    useEffect(() => {
        setLoading(true);
        getSaleById(saleId).then(async (fetchedSale) => {
            const catalog = await findProductsByQueryString('');
            const productsCopy = (catalog || []).map((p) => ({...p, quantity: 0, printStatus: 0, paymentStatus: 0}));
            const extraItems = [];

            (fetchedSale.saleItems || []).forEach((item) => {
                const existing = productsCopy.find((p) => p.id === item.product.id && p.name === item.productName);
                if (existing) {
                    existing.quantity = item.quantity;
                    existing.printStatus = item.printStatus;
                    existing.paymentStatus = item.paymentStatus;
                } else {
                    const base = productsCopy.find((p) => p.id === item.product.id);
                    extraItems.push({
                        ...(base || {id: item.product.id, salePrice: item.product.salePrice}),
                        name: item.productName,
                        quantity: item.quantity,
                        printStatus: item.printStatus,
                        paymentStatus: item.paymentStatus
                    });
                }
            });

            const merged = productsCopy.concat(extraItems);
            setSale(fetchedSale);
            setProducts(merged);
            setFilteredProducts(JSON.parse(JSON.stringify(merged)));
            setLoading(false);
        });
    }, [saleId]);

    useEffect(() => {
        if (inputQueryString.current) {
            inputQueryString.current.focus();
        }
    }, [sale]);

    useEffect(() => {
        const newTotalValue = products.filter((p) => p.quantity > 0).reduce((accumulator, p) => accumulator + (p.salePrice * p.quantity), 0);
        setTotalValue(newTotalValue);
    }, [products]);

    useEffect(() => {
        const filtered = products.filter((f) => removeSpecialChars(f.name).toUpperCase().includes(queryString));
        setFilteredProducts(JSON.parse(JSON.stringify(filtered)));
    }, [queryString, products]);

    const handleInputChange = (e) => {
        setQueryString(removeSpecialChars(e.target.value).toUpperCase());
    };

    function onIncrement(index, product) {
        setProducts((prevItems) => prevItems.map((item) =>
            item.id === product.id && item.name === product.name ? {...item, quantity: (item.quantity ? item.quantity : 0) + 1} : item
        ));
        setFilteredProducts((prevItems) => prevItems.map((item, i) =>
            i === index ? {...item, quantity: (item.quantity ? item.quantity : 0) + 1} : item
        ));
    }

    function onDecrement(index, product) {
        setProducts((prevItems) => prevItems.map((item) =>
            item.id === product.id && item.name === product.name ? {...item, quantity: (item.quantity ? item.quantity : 0) - 1} : item
        ));
        setFilteredProducts((prevItems) => prevItems.map((item, i) =>
            i === index ? {...item, quantity: (item.quantity ? item.quantity : 0) - 1} : item
        ));
    }

    const isPaid = sale && sale.saleItems.length > 0 && sale.saleItems.every((item) => item.paymentStatus === 1);
    const isBalcaoOrder = sale && (sale.customer === undefined || sale.customer === null) && (sale.table === undefined || sale.table === null);

    async function onConfirm() {
        if (isPaid) {
            // paymentStatus não precisa ser informado aqui: o backend sempre marca como pago
            // quando a venda tem formas de pagamento anexadas (sempre o caso neste fluxo).
            const saleItems = products
                .filter((p) => p.quantity > 0)
                .map((p) => ({product: {id: p.id, salePrice: p.salePrice}, productName: p.name, quantity: p.quantity}));

            if (saleItems.length === 0) {
                alert('Nenhum item foi adicionado.');
                return;
            }

            const {paymentMethods, saleItems: originalItems, ...saleBase} = sale;
            const existingPayments = (paymentMethods || []).map((pm) => ({paymentMethod: pm.paymentMethod, value: pm.value}));

            localStorage.setItem('saleClosing', JSON.stringify({
                mode: 'edit',
                sale: {...saleBase, saleItems},
                totalValue,
                existingPayments
            }));

            navigate(isBalcaoOrder && deliveryEnabled ? '/sale/delivery' : '/sale/payment');
            return;
        }

        const saleItems = products
            .filter((p) => p.quantity > 0)
            .map((p) => ({
                product: {id: p.id, salePrice: p.salePrice},
                productName: p.name,
                quantity: p.quantity,
                printStatus: p.printStatus ?? 0,
                paymentStatus: p.paymentStatus ?? 0
            }));

        if (saleItems.length === 0) {
            alert('Nenhum item foi adicionado.');
            return;
        }

        /* eslint-disable no-restricted-globals */
        if (!confirm('Confirma a alteração deste pedido?')) {
            return;
        }

        const {paymentMethods, saleItems: originalItems, ...saleBase} = sale;

        try {
            setLoading(true);
            await updateSale({...saleBase, saleItems});
            navigate('/sale/sales');
        } catch (error) {
            console.error('Error updating sale:', error);
            alert('Não foi possível salvar o pedido.');
        } finally {
            setLoading(false);
        }
    }

    if (!sale) {
        return null;
    }

    const customerName = sale.customer ? sale.customer.name : sale.customerName;

    return (
        <>
            <div className="header-label">Editar Pedido {customerName ? `- ${customerName}` : ''}</div>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}
                 className="input-container">
                <input style={{width: '100%'}} ref={inputQueryString} type="text" value={queryString}
                       onChange={handleInputChange} onFocus={(event) => event.target.select()}/>
                <button id="clearButton" onClick={() => {
                    setQueryString('');
                    inputQueryString.current.focus();
                }}>X
                </button>
            </div>
            <div className="scrollable-table-area">
            <table style={{width: '100%'}}>
                <colgroup>
                    <col style={{width: '300px'}}/>
                    <col style={{width: '50px'}}/>
                    <col style={{width: '50px'}}/>
                </colgroup>
                <thead>
                <tr>
                    <th>Nome</th>
                    <th>Preço</th>
                    <th/>
                    <th/>
                    <th/>
                </tr>
                </thead>
                <tbody style={{overflowY: 'auto'}}>
                {filteredProducts.map((product, index) => (
                    <tr key={index}
                        style={{backgroundColor: product.quantity > 0 ? 'var(--warning-color)' : 'white'}}>
                        <td>
                            <div>{product.name}</div>
                        </td>
                        <td>
                            <div style={{textAlign: 'right'}}>{product.salePrice.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}</div>
                        </td>
                        <td>
                            <button className="remove-button button-minus" onClick={() => onDecrement(index, product)}>-</button>
                        </td>
                        <td>
                            {product.quantity ? product.quantity : 0}
                        </td>
                        <td>
                            <button className="button-plus" onClick={() => onIncrement(index, product)}>+</button>
                        </td>
                    </tr>
                ))}
                </tbody>
                {filteredProducts.length === 0 &&
                    <tfoot>
                    <tr>
                        <td colSpan={5}>Nenhum item encontrado</td>
                    </tr>
                    </tfoot>
                }
            </table>
            </div>
            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div className="total-label" style={{textAlign: 'right'}}>Valor Total:
                    R$ {totalValue.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
            </div>
            <ActionButtonsManagerView>
                <button onClick={() => navigate('/sale/sales')} className="back-button">Cancelar</button>
                <button onClick={onConfirm} disabled={loading}>Conferir</button>
            </ActionButtonsManagerView>
        </>
    );
};

export default SaleManagerEditItems;
